// apps/landing-page/src/hooks/useSegmentPolicy.ts
'use client';

import { useEffect, useState } from 'react';
import { catalogApi, type SegmentExamType } from '@/lib/api';
import type { AudienceSegment } from '@/context/PreferencesContext';

export type SegmentPolicyStatus = 'loading' | 'ready' | 'error';

export interface UseSegmentPolicyResult {
  examTypes: SegmentExamType[];
  status: SegmentPolicyStatus;
}

// Module-scoped so every component instance (PersonalizationStrip, Catalog,
// the /tests bundle page, …) shares one cache — a given segment is fetched
// from `GET /v1/catalog/segment-policy` at most once per page load. Segments
// are a closed 3-value enum, so this cache can't grow unbounded.
const policyCache = new Map<string, SegmentExamType[]>();
const inFlight = new Map<string, Promise<SegmentExamType[]>>();

function loadSegmentPolicy(segment: string): Promise<SegmentExamType[]> {
  const cached = policyCache.get(segment);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(segment);
  if (pending) return pending;

  const request = catalogApi
    .getSegmentPolicy(segment)
    .then((types) => {
      policyCache.set(segment, types);
      inFlight.delete(segment);
      return types;
    })
    .catch((error: unknown) => {
      inFlight.delete(segment);
      throw error;
    });

  inFlight.set(segment, request);
  return request;
}

/**
 * Segment-resolved, ordered, visible exam types for the guest/user's
 * audience segment — deduped and cached across every consumer on the page.
 * `segment === null` means no preference set yet: returns an empty, 'ready'
 * result without ever calling the API, matching the existing "no pref = full
 * catalog" fallback used by every consumer.
 */
export function useSegmentPolicy(segment: AudienceSegment | null): UseSegmentPolicyResult {
  const [examTypes, setExamTypes] = useState<SegmentExamType[]>(() =>
    segment ? policyCache.get(segment) ?? [] : [],
  );
  const [status, setStatus] = useState<SegmentPolicyStatus>(() => {
    if (!segment) return 'ready';
    return policyCache.has(segment) ? 'ready' : 'loading';
  });

  useEffect(() => {
    if (!segment) {
      setExamTypes([]);
      setStatus('ready');
      return;
    }

    const cached = policyCache.get(segment);
    if (cached) {
      setExamTypes(cached);
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    loadSegmentPolicy(segment)
      .then((types) => {
        if (cancelled) return;
        setExamTypes(types);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setExamTypes([]);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [segment]);

  return { examTypes, status };
}
